import type { CallbackInterface } from 'recoil'

import { logAppError } from 'Helpers/logger'

import type { PageStateType } from 'Types/state'

import { loaderUpdater, pageState } from 'State/page'

import type { ModalRecipientGroupsState } from 'Pages/RecipientsList/model'

import { fetchPagePreferences } from 'Pages/RecipientsList/recoil/services/filters'
import { fetchRecipientGroups } from 'Pages/RecipientsList/recoil/services/groups'
import { fetchRecipientContact } from 'Pages/RecipientsList/recoil/services/recipient'
import { modalRecipientGroupsState } from 'Pages/RecipientsList/recoil/state/modalRecipientGroups'

export const modalRecipientGroupsOpenCallback =
  ({ set }: CallbackInterface) =>
  async (idRecipient: number) => {
    set<PageStateType>(pageState, loaderUpdater(true))

    try {
      const [{ groups: groupsPrefs }, contact] = await Promise.all([
        fetchPagePreferences(),
        fetchRecipientContact(idRecipient),
      ])

      const groups = await fetchRecipientGroups(idRecipient, {
        pageNumber: 1,
        pageSize: 5,
        orderBy: groupsPrefs.orderBy,
        orderDescending: groupsPrefs.orderDescending,
      })

      set<ModalRecipientGroupsState>(modalRecipientGroupsState, (prev) => ({
        ...prev,
        ...groups,
        idRecipient,
        contact,
        show: true,
        status: 'ready',
      }))
    } catch (error) {
      console.error('### error', error)

      logAppError({
        Component: 'RecipientsList',
        Method: 'modalRecipientGroupsOpenCallback',
        appError: error,
      })

      set<ModalRecipientGroupsState>(modalRecipientGroupsState, (prev) => ({
        ...prev,
        status: 'error',
      }))
    }

    set<PageStateType>(pageState, loaderUpdater(false))
  }

export const modalRecipientGroupsCloseCallback =
  ({ reset }: CallbackInterface) =>
  async () => {
    reset(modalRecipientGroupsState)
  }

export const modalRecipientGroupsFetchGroupsCallback =
  ({ set, snapshot }: CallbackInterface) =>
  async ({
    pageNumber,
    pageSize,
    orderBy,
  }: {
    pageNumber?: number
    pageSize?: number
    orderBy?: 'IdGroup' | 'GroupName' | 'AssignmentDate'
  }) => {
    const { idRecipient, ...prevPagination } =
      await snapshot.getPromise<ModalRecipientGroupsState>(
        modalRecipientGroupsState,
      )

    set(modalRecipientGroupsState, (prev) => ({ ...prev, loading: true }))

    try {
      const newPagination = {
        pageNumber:
          pageNumber !== undefined ? pageNumber : prevPagination.pageNumber,
        pageSize: pageSize !== undefined ? pageSize : prevPagination.pageSize,
        orderBy: orderBy !== undefined ? orderBy : prevPagination.orderBy,
        orderDescending:
          orderBy !== undefined && orderBy === prevPagination.orderBy
            ? !prevPagination.orderDescending
            : prevPagination.orderDescending,
      }

      const groups = await fetchRecipientGroups(idRecipient, newPagination)

      set<ModalRecipientGroupsState>(modalRecipientGroupsState, (prev) => ({
        ...prev,
        ...groups,
        orderBy: newPagination.orderBy,
        orderDescending: newPagination.orderDescending,
      }))
    } catch (error) {
      console.error('### error', error)

      logAppError({
        Component: 'RecipientsList',
        Method: 'modalRecipientGroupsFetchGroupsCallback',
        appError: error,
      })

      set<ModalRecipientGroupsState>(modalRecipientGroupsState, (prev) => ({
        ...prev,
        status: 'error',
      }))
    }

    set(modalRecipientGroupsState, (prev) => ({ ...prev, loading: false }))
  }
